import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheck, FaEnvelopeOpenText, FaFileSignature } from "react-icons/fa";

import { DocumentTypeContext } from "../../context/DocumentContext";
import { UserTypeContext } from "../../context/UserTypeContext";
import { AuthContext } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import "./SignSuccess.css";

const SignSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();

  const {
    signers,
    document: documentData,
    infoSign,
    setInfoSign,
    getDocumentSign,
    getSigners,
  } = useContext(DocumentTypeContext);

  const { formValuesUsers, getUsersById } = useContext(UserTypeContext);
  const { decryptToken } = useContext(AuthContext);

  const [bootstrapping, setBootstrapping] = useState(false);

  const url = window.location.href;
  const urlObj = useMemo(() => new URL(url), [url]);
  const tokenParam = urlObj.searchParams.get("token");
  const documentIdParam = urlObj.searchParams.get("documentId");

  useEffect(() => {
    const ensureContext = async () => {
      if (signers && signers.length > 0) return;

      setBootstrapping(true);
      try {
        if (tokenParam) {
          const decoded = decodeURIComponent(tokenParam).replace(/ /g, "+");
          const result = await decryptToken(decoded);
          if (result?.data?.data) {
            const { documentId, userId } = result.data.data;
            setInfoSign(result.data.data);
            await Promise.all([
              getDocumentSign(documentId, true),
              getSigners(documentId, true),
              getUsersById(userId, true),
            ]);
          }
        } else if (documentIdParam) {
          await getSigners(documentIdParam, true);
        }
      } catch (error) {
        showToast("error", "Não foi possível atualizar o status do documento.");
      } finally {
        setBootstrapping(false);
      }
    };

    ensureContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = signers?.length || 0;
  const signedCount =
    signers?.filter?.((s: any) => s.hasSigned)?.length || 0;
  const allSigned = total > 0 && signedCount === total;
  const progressPct = total === 0 ? 0 : Math.round((signedCount / total) * 100);

  const goBackToDocument = () => {
    const params = new URLSearchParams(location.search);
    navigate({
      pathname: "/admin/sign-document",
      search: params.toString() ? `?${params.toString()}` : "",
    });
  };

  return (
    <div className="signsuccess">
      <div className="signsuccess__inner">
        <div className="signsuccess__hero">
          <div className="signsuccess__check">
            <svg
              className="signsuccess__check-svg"
              viewBox="0 0 52 52"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="signsuccess__check-circle"
                cx="26"
                cy="26"
                r="25"
                fill="none"
              />
              <path
                className="signsuccess__check-mark"
                fill="none"
                d="M14 27 l8 8 l16 -16"
              />
            </svg>
          </div>

          <span className="signsuccess__eyebrow">Assinatura registrada</span>
          <h1 className="signsuccess__title">
            Pronto, {formValuesUsers?.name?.split(" ")[0] || "tudo certo"}!
          </h1>
          <p className="signsuccess__subtitle">
            Sua assinatura foi registrada com segurança no documento{" "}
            <strong>{infoSign?.documentName || documentData?.documentName || "selecionado"}</strong>.
          </p>
        </div>

        <div className="signsuccess__panel">
          <div className="signsuccess__panel-header">
            <div>
              <span className="signsuccess__panel-eyebrow">
                <FaFileSignature /> Progresso das assinaturas
              </span>
              <strong className="signsuccess__panel-count">
                {bootstrapping
                  ? "Atualizando..."
                  : `${signedCount} de ${total} signatário${total === 1 ? "" : "s"} já assin${signedCount === 1 ? "ou" : "aram"}`}
              </strong>
            </div>
            <span className="signsuccess__panel-pct">{progressPct}%</span>
          </div>

          <div className="signsuccess__progress">
            <div
              className="signsuccess__progress-bar"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {signers?.length > 0 && (
            <ul className="signsuccess__signers">
              {signers.map((signer: any, idx: number) => (
                <li
                  key={`${signer.email || signer.name}-${idx}`}
                  className={`signsuccess__signer ${
                    signer.hasSigned ? "signsuccess__signer--signed" : ""
                  }`}
                >
                  <div className="signsuccess__signer-avatar">
                    {(signer.signerName || signer.name || "?")
                      .toString()
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div className="signsuccess__signer-info">
                    <span className="signsuccess__signer-name">
                      {signer.signerName || signer.name}
                    </span>
                    <span className="signsuccess__signer-email">
                      {signer.email || signer.name}
                    </span>
                  </div>
                  <span
                    className={`signsuccess__signer-status ${
                      signer.hasSigned ? "is-signed" : "is-pending"
                    }`}
                  >
                    {signer.hasSigned ? (
                      <>
                        <FaCheck /> Assinado
                      </>
                    ) : (
                      "Pendente"
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className={`signsuccess__info ${
            allSigned ? "signsuccess__info--success" : ""
          }`}
        >
          <div className="signsuccess__info-icon">
            <FaEnvelopeOpenText />
          </div>
          <div className="signsuccess__info-text">
            {allSigned ? (
              <>
                <strong>Documento finalizado!</strong>
                <p>
                  Todas as assinaturas foram coletadas. Em alguns instantes,
                  todos os signatários receberão o documento final assinado por
                  e-mail.
                </p>
              </>
            ) : (
              <>
                <strong>Aguardando os demais signatários</strong>
                <p>
                  Assim que todos assinarem, você receberá o documento final
                  assinado em <strong>{formValuesUsers?.email || "seu e-mail"}</strong>.
                  Pode fechar esta janela com tranquilidade.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="signsuccess__actions">
          <button
            type="button"
            className="signsuccess__btn signsuccess__btn--primary"
            onClick={goBackToDocument}
          >
            Visualizar documento
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignSuccess;
